import React, { useMemo, useCallback } from 'react';
import { ShiftRequest } from '../../types';
import { REQUEST_TYPE_INFO, REQUEST_STATUS_INFO } from '../../constants';
import { useDateFormatter } from '../../hooks';
import styles from './ShiftRequestCard.module.css';

interface ShiftRequestCardProps {
  request: ShiftRequest;
  onApprove?: (id: number) => void;
  onReject?: (id: number) => void;
  onCancel?: (id: number) => void;
  showActions?: boolean;
  canReview?: boolean;
}

const ShiftRequestCardComponent: React.FC<ShiftRequestCardProps> = ({
  request,
  onApprove,
  onReject,
  onCancel,
  showActions = true,
  canReview = false
}) => {
  const { formatDate, formatDateTime } = useDateFormatter();

  // Memoize status badge to avoid recreation on every render
  const statusBadge = useMemo(() => {
    const badgeInfo = REQUEST_STATUS_INFO[request.status] || REQUEST_STATUS_INFO['Pending'];
    return (
      <span className={`${styles.badge} ${styles[badgeInfo.className]}`}>
        {badgeInfo.label}
      </span>
    );
  }, [request.status]);

  // Memoize type badge to avoid recreation on every render
  const typeBadge = useMemo(() => {
    const typeInfo = REQUEST_TYPE_INFO[request.type] || REQUEST_TYPE_INFO['Leave'];
    return (
      <span className={styles.typeBadge}>
        {typeInfo.icon} {typeInfo.label}
      </span>
    );
  }, [request.type]);

  // Memoize pending status check
  const isPending = useMemo(() => request.status === 'Pending', [request.status]);

  // Memoize callback handlers to prevent prop changes
  const handleApprove = useCallback(() => {
    onApprove?.(request.id);
  }, [onApprove, request.id]);

  const handleReject = useCallback(() => {
    onReject?.(request.id);
  }, [onReject, request.id]);

  const handleCancel = useCallback(() => {
    onCancel?.(request.id);
  }, [onCancel, request.id]);

  return (
    <div className={styles.card}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          {typeBadge}
          {statusBadge}
        </div>
        <div className={styles.requestId}>#{request.id}</div>
      </div>

      <div className={styles.content}>
        <div className={styles.field}>
          <span className={styles.label}>👤 Dipendente:</span>
          <span className={styles.value}>{request.employeeName}</span>
        </div>

        <div className={styles.field}>
          <span className={styles.label}>📅 Data Inizio:</span>
          <span className={styles.value}>{formatDate(request.requestedDateStart)}</span>
        </div>

        {request.requestedDateEnd && (
          <div className={styles.field}>
            <span className={styles.label}>📅 Data Fine:</span>
            <span className={styles.value}>{formatDate(request.requestedDateEnd)}</span>
          </div>
        )}

        {request.reason && (
          <div className={styles.field}>
            <span className={styles.label}>💬 Motivazione:</span>
            <span className={styles.value}>{request.reason}</span>
          </div>
        )}

        {request.shiftId && (
          <div className={styles.field}>
            <span className={styles.label}>🔄 Turno Target:</span>
            <span className={styles.value}>Turno #{request.shiftId}</span>
          </div>
        )}

        {request.approvedBy && (
          <div className={styles.field}>
            <span className={styles.label}>✅ Approvato da:</span>
            <span className={styles.value}>{request.approvedByName}</span>
          </div>
        )}

        {request.approvedAt && (
          <div className={styles.field}>
            <span className={styles.label}>🕐 Data Approvazione:</span>
            <span className={styles.value}>{formatDateTime(request.approvedAt)}</span>
          </div>
        )}

        {request.rejectionReason && (
          <div className={styles.field}>
            <span className={styles.label}>📝 Motivo Rifiuto:</span>
            <span className={styles.value}>{request.rejectionReason}</span>
          </div>
        )}

        <div className={styles.field}>
          <span className={styles.label}>🕐 Creata il:</span>
          <span className={styles.value}>{formatDateTime(request.createdAt)}</span>
        </div>
      </div>

      {showActions && (
        <div className={styles.actions}>
          {canReview && isPending && (
            <>
              {onApprove && (
                <button
                  className={`${styles.actionButton} ${styles.approve}`}
                  onClick={handleApprove}
                >
                  ✅ Approva
                </button>
              )}
              {onReject && (
                <button
                  className={`${styles.actionButton} ${styles.reject}`}
                  onClick={handleReject}
                >
                  ❌ Rifiuta
                </button>
              )}
            </>
          )}
          {!canReview && isPending && onCancel && (
            <button
              className={`${styles.actionButton} ${styles.cancel}`}
              onClick={handleCancel}
            >
              🗑️ Cancella
            </button>
          )}
        </div>
      )}
    </div>
  );
};

// Memoize the component to prevent unnecessary re-renders
export const ShiftRequestCard = React.memo(ShiftRequestCardComponent);

ShiftRequestCard.displayName = 'ShiftRequestCard';
